import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderSection } from "@/components/studio/builder-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { ReadOnlyBanner } from "@/components/studio/read-only-banner";
import { RefinementSection } from "@/components/studio/refinement-section";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { ContentRecipeService } from "@/lib/api/recipe.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type {
  Category,
  Coach,
  RecipeContent,
  RecipeDietTag,
  RecipeLanguageVariant,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import { RECIPE_DIET_TAGS, RECIPE_DIET_TAG_LABELS } from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { validateThumbnailFile } from "@/lib/thumbnail-validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { RecipeVariantFields, recipeFormError } from "./recipe-variant-fields";

const recipeConfig = CONTENT_TYPE_CONFIG.recipe;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

interface RecipeEditData extends RecipeContent {
  variants: RecipeLanguageVariant[];
}

interface VariantEdit {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
  aboutLongDescription: string;
  localizedIngredients: string[];
  localizedSteps: string[];
  userTips: string[];
  thumbnailUrl: string | null;
  prepTimeMinutes: number | null;
  cookTimeMinutes: number | null;
}

const emptyEditVariant = (
  languageCode: string,
  isDefault: boolean,
): VariantEdit => ({
  languageCode,
  isDefault,
  localizedTitle: "",
  localizedDescription: "",
  aboutLongDescription: "",
  localizedIngredients: [],
  localizedSteps: [],
  userTips: [],
  thumbnailUrl: null,
  prepTimeMinutes: null,
  cookTimeMinutes: null,
});

export default function RecipeEditPage({ id }: { id: string }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const guard = (forSubmit: boolean, run: () => void) => {
    const error = recipeFormError(categoryId, variants, forSubmit);
    if (error) toast({ title: error, variant: "destructive" });
    else run();
  };
  const { uploadFile, isUploading } = useUpload();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const selfCoachId = user?.id ?? null;

  const [categoryId, setCategoryId] = useState("");
  const [selectedTagOptionIds, setSelectedTagOptionIds] = useState<string[]>(
    [],
  );
  const [internalNotes, setInternalNotes] = useState("");
  const [authorCoachId, setAuthorCoachId] = useState<string | null>(null);
  const [dietTags, setDietTags] = useState<RecipeDietTag[]>([]);
  const [servings, setServings] = useState<number | null>(null);
  const [calories, setCalories] = useState<number | null>(null);
  const [variants, setVariants] = useState<VariantEdit[]>([]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const { data: recipeData, isLoading: isLoadingRecipe } =
    useQuery<RecipeEditData>({
      queryKey: ["/content-recipes", id],
      queryFn: async () => {
        const recipe = await ContentRecipeService.findOne(id);
        const versions = (recipe as any).versions || [];
        const latestVersion = versions[0] || {};
        return {
          ...recipe,
          variants: latestVersion.languages || [],
          version: latestVersion.version,
          status: latestVersion.status,
          internalNotes: latestVersion.internalNotes,
        } as RecipeEditData;
      },
    });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  useEffect(() => {
    if (isCoachOnly && selfCoachId && !authorCoachId && loaded) {
      setAuthorCoachId(selfCoachId);
    }
  }, [isCoachOnly, selfCoachId, authorCoachId, loaded]);

  useEffect(() => {
    if (recipeData && !loaded) {
      setCategoryId(recipeData.categoryId ?? "");
      setSelectedTagOptionIds(recipeData.tagOptionIds);
      setInternalNotes(recipeData.internalNotes || "");
      setAuthorCoachId(recipeData.authorCoachId ?? null);
      setDietTags(recipeData.dietTags || []);
      setServings(recipeData.servings ?? null);
      setCalories(recipeData.calories ?? null);
      setVariants(
        recipeData.variants.map((v) => ({
          id: v.id,
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle,
          localizedDescription: v.localizedDescription || "",
          aboutLongDescription: v.aboutLongDescription || "",
          localizedIngredients: v.localizedIngredients || [],
          localizedSteps: v.localizedSteps || [],
          userTips: v.userTips || [],
          thumbnailUrl: v.thumbnailUrl ?? null,
          prepTimeMinutes: v.prepTimeMinutes ?? null,
          cookTimeMinutes: v.cookTimeMinutes ?? null,
        })),
      );
      setLoaded(true);
    }
  }, [recipeData, loaded]);

  const buildSavePayload = () => {
    const defaultVariant = variants.find((v) => v.isDefault) ?? variants[0];
    return {
      recipe: {
        categoryId,
        tagOptionIds: selectedTagOptionIds,
        primaryLanguage: defaultVariant?.languageCode,
        internalNotes: internalNotes || null,
        authorCoachId,
        dietTags,
        servings,
        calories,
      },
      variants: variants.map((v) => ({
        languageCode: v.languageCode,
        isDefault: v.isDefault,
        localizedTitle: v.localizedTitle,
        localizedDescription: v.localizedDescription || null,
        aboutLongDescription: v.aboutLongDescription || null,
        localizedIngredients: v.localizedIngredients,
        localizedSteps: v.localizedSteps,
        userTips: v.userTips,
        thumbnailUrl: v.thumbnailUrl,
        prepTimeMinutes: v.prepTimeMinutes,
        cookTimeMinutes: v.cookTimeMinutes,
        translationStatus: v.isDefault ? "completed" : "pending",
      })),
    };
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      await ContentRecipeService.update(id, buildSavePayload());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-recipes"] });
      queryClient.invalidateQueries({ queryKey: ["/content-recipes", id] });
      toast({ title: "Recipe saved" });
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to save",
        variant: "destructive",
      });
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      await ContentRecipeService.update(id, buildSavePayload());
      await ContentRecipeService.submitForReview(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-recipes"] });
      queryClient.invalidateQueries({ queryKey: ["/content-recipes", id] });
      toast({ title: "Submitted for approval" });
      setLocation("/studio/recipes");
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to submit",
        variant: "destructive",
      });
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: async () => {
      return ContentRecipeService.createVersion(id);
    },
    onSuccess: (data: { id: string; version: number }) => {
      queryClient.invalidateQueries({ queryKey: ["/content-recipes"] });
      toast({ title: `New version (v${data.version}) created as draft` });
      setLocation(`/studio/recipes/${data.id}/edit`);
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to create new version",
        variant: "destructive",
      });
    },
  });

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "thumbnailUrl",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = await validateThumbnailFile(file);
    if (!validation.valid) {
      toast({
        title: "Invalid thumbnail",
        description: validation.error,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    const result = await uploadFile(file);
    if (result) {
      const updated = [...variants];
      updated[activeVariantIndex] = {
        ...updated[activeVariantIndex],
        [field]: result.objectPath,
      };
      setVariants(updated);
    }
  };

  const updateVariant = (field: keyof VariantEdit, value: any) => {
    const updated = [...variants];
    updated[activeVariantIndex] = {
      ...updated[activeVariantIndex],
      [field]: value,
    };
    setVariants(updated);
  };

  const addLanguageVariant = (langCode: string) => {
    if (variants.some((v) => v.languageCode === langCode)) return;
    setVariants([...variants, emptyEditVariant(langCode, false)]);
    setActiveVariantIndex(variants.length);
  };

  const confirmRemoveLanguageVariant = () => {
    if (pendingRemoveIndex === null) return;
    if (variants[pendingRemoveIndex].isDefault) return;
    if (variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    const updated = variants.filter((_, i) => i !== pendingRemoveIndex);
    setVariants(updated);
    if (activeVariantIndex >= updated.length) {
      setActiveVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingRemoveIndex(null);
  };

  const setAsDefault = (index: number) => {
    if (index < 0 || index >= variants.length) return;
    const updated = variants.map((v, i) => ({ ...v, isDefault: i === index }));
    setVariants(updated);
    setActiveVariantIndex(index);
  };

  const toggleTagOption = (tagId: string) => {
    setSelectedTagOptionIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  const activeVariant = variants[activeVariantIndex];

  const { withdrawMutation, isReadOnly, isOwner, canCreateVersion } =
    useContentLifecycle({
    contentType: "recipe",
    apiPath: "/content-recipes",
    id,
    status: recipeData?.status,
    createdBy: recipeData?.createdBy,
    authorCoachId: recipeData?.authorCoachId,
    listRoute: "/studio/recipes",
    editRoute: (newId) => `/studio/recipes/${newId}/edit`,
  });

  const primaryTitle =
    variants.find((v) => v.isDefault)?.localizedTitle || undefined;

  return (
    <BuilderShell
      contentTypeConfig={recipeConfig}
      mode="edit"
      title={primaryTitle}
      version={recipeData?.version}
      status={recipeData?.status}
      isLoading={isLoadingRecipe}
      notFound={!isLoadingRecipe && !recipeData}
      isReadOnly={isReadOnly}
      isSaving={saveMutation.isPending}
      isSubmitting={submitMutation.isPending}
      isCreatingVersion={createVersionMutation.isPending}
      canCreateVersion={canCreateVersion}
      isWithdrawing={withdrawMutation.isPending}
      onSave={() => guard(false, () => saveMutation.mutate())}
      onSubmit={() => guard(true, () => submitMutation.mutate())}
      onWithdraw={() => withdrawMutation.mutate(undefined)}
      onCreateVersion={() => createVersionMutation.mutate()}
    >
      {isReadOnly && <ReadOnlyBanner ownedByOther={!isOwner} />}

      <IntentTaggingSection
        stepNumber={1}
        categories={categories}
        categoryId={categoryId}
        onCategoryChange={setCategoryId}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={selectedTagOptionIds}
        onToggleTagOption={toggleTagOption}
        isReadOnly={isReadOnly}
      />

      <BuilderSection
        stepNumber={2}
        title="Author"
        description="Select the coach attributed as the recipe's author"
        defaultOpen={true}
        disabled={isReadOnly}
      >
        <div className="space-y-2">
          <Label>Author Coach</Label>
          {isCoachOnly ? (
            <Input
              value={
                (recipeData as any)?.author?.name ??
                (authorCoachId === user?.id ? (user?.name ?? "") : "")
              }
              disabled
              data-testid="select-author-coach"
            />
          ) : (
            <Select
              value={authorCoachId || "none"}
              onValueChange={(val) =>
                setAuthorCoachId(val === "none" ? null : val)
              }
              disabled={isReadOnly}
            >
              <SelectTrigger data-testid="select-author-coach">
                <SelectValue placeholder="Select author" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No author</SelectItem>
                {coaches.map((coach) => (
                  <SelectItem key={coach.id} value={coach.id}>
                    {coach.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </BuilderSection>

      <BuilderSection
        stepNumber={3}
        title="Nutrition & Diet"
        description="Diet tags, servings, and calorie info — shared across all language variants"
        defaultOpen={true}
        disabled={isReadOnly}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Diet Tags</Label>
            <div className="flex flex-wrap gap-2" data-testid="diet-tags-list">
              {RECIPE_DIET_TAGS.map((tag) => {
                const selected = dietTags.includes(tag);
                return (
                  <Badge
                    key={tag}
                    variant={selected ? "default" : "outline"}
                    className={
                      isReadOnly
                        ? "cursor-default"
                        : "cursor-pointer hover-elevate active-elevate-2"
                    }
                    onClick={() => {
                      if (isReadOnly) return;
                      setDietTags(
                        selected
                          ? dietTags.filter((t) => t !== tag)
                          : [...dietTags, tag],
                      );
                    }}
                    data-testid={`diet-tag-${tag}`}
                  >
                    {RECIPE_DIET_TAG_LABELS[tag]}
                  </Badge>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Servings</Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={servings ?? ""}
                onChange={(e) =>
                  setServings(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  )
                }
                placeholder="e.g. 4"
                disabled={isReadOnly}
                data-testid="input-servings"
              />
            </div>
            <div className="space-y-2">
              <Label>Calories (per serving)</Label>
              <Input
                type="number"
                min={0}
                step={1}
                value={calories ?? ""}
                onChange={(e) =>
                  setCalories(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  )
                }
                placeholder="e.g. 320"
                disabled={isReadOnly}
                data-testid="input-calories"
              />
            </div>
          </div>
        </div>
      </BuilderSection>

      <LanguageVariantsSection
        stepNumber={4}
        variants={variants}
        activeVariantIndex={activeVariantIndex}
        onActiveVariantChange={setActiveVariantIndex}
        availableLanguages={availableLanguages}
        onAddVariant={addLanguageVariant}
        onSetDefault={setAsDefault}
        pendingRemoveIndex={pendingRemoveIndex}
        onRequestRemove={setPendingRemoveIndex}
        onConfirmRemove={confirmRemoveLanguageVariant}
        onCancelRemove={() => setPendingRemoveIndex(null)}
        isReadOnly={isReadOnly}
      >
        {activeVariant && (
          <RecipeVariantFields
            variant={activeVariant}
            onFieldChange={updateVariant}
            onFileUpload={handleFileUpload}
            isUploading={isUploading}
            isReadOnly={isReadOnly}
          />
        )}
      </LanguageVariantsSection>

      <RefinementSection
        stepNumber={5}
        internalNotes={internalNotes}
        onInternalNotesChange={setInternalNotes}
        isReadOnly={isReadOnly}
      />
    </BuilderShell>
  );
}
